import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * Safeguards for templates rendered *live* inside small catalog cards.
 *
 * - `CardMediaGuard` neutralises any <video>/<audio> a template mounts inside a card: it
 *   removes autoplay, forces preload="none", strips the src (so nothing is downloaded or
 *   decoded) and pauses anything that still tries to play. It runs in a layout effect (before
 *   the browser's async resource selection starts) and keeps watching with a MutationObserver
 *   so media added later (scene changes, lazy sections) is caught too. The poster frame and the
 *   rest of the template's markup still render, so the card looks the same.
 *   Full-screen Preview, the selected-template preview and published invites are unaffected —
 *   the guard is only used by TemplateCardPreview.
 * - `LazyInView` mounts its children only once the card scrolls near the viewport, so a grid of
 *   26 templates does not mount 26 live renders up front.
 *
 * Limitation: media created with `new Audio()` (never attached to the DOM) is invisible to the
 * guard. Templates that do that are shown as recorded posters in cards instead.
 */

export function neutralizeMedia(root: ParentNode): number {
  let count = 0;
  root.querySelectorAll('video, audio').forEach((node) => {
    const media = node as HTMLMediaElement;
    count += 1;
    if (media.hasAttribute('autoplay')) media.removeAttribute('autoplay');
    media.autoplay = false;
    if (media.getAttribute('preload') !== 'none') media.setAttribute('preload', 'none');
    let stripped = false;
    if (media.hasAttribute('src')) {
      media.removeAttribute('src');
      stripped = true;
    }
    media.querySelectorAll('source[src]').forEach((source) => {
      source.removeAttribute('src');
      stripped = true;
    });
    try {
      if (!media.paused) media.pause();
      if (stripped) media.load();
    } catch {
      /* jsdom / detached element */
    }
  });
  return count;
}

export function CardMediaGuard({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    neutralizeMedia(el);
    const observer =
      typeof MutationObserver !== 'undefined' ? new MutationObserver(() => neutralizeMedia(el)) : null;
    observer?.observe(el, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'autoplay'] });
    // `play` does not bubble, so listen in the capture phase.
    const onPlay = (event: Event) => {
      const target = event.target;
      if (target instanceof HTMLMediaElement) {
        try {
          target.pause();
        } catch {
          /* ignore */
        }
      }
    };
    el.addEventListener('play', onPlay, true);
    return () => {
      observer?.disconnect();
      el.removeEventListener('play', onPlay, true);
    };
  }, []);

  return (
    <div ref={ref} data-card-media-guard="" className="h-full w-full">
      {children}
    </div>
  );
}

export function LazyInView({
  children,
  placeholder = null,
  rootMargin = '300px',
}: {
  children: React.ReactNode;
  placeholder?: React.ReactNode;
  rootMargin?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // Without IntersectionObserver (very old browsers) render immediately, as before.
  const [visible, setVisible] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (visible) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref} data-lazy-in-view={visible ? 'mounted' : 'pending'} className="h-full w-full">
      {visible ? children : placeholder}
    </div>
  );
}
